import {
  useState,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {Box, Typography, IconButton} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  addGradientStop,
  updateGradientStop,
  removeGradientStop,
  selectActiveScene,
  generateStopId,
} from '../../../common/components/store/scenesSlice'
import type {GradientStop} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {useTranslation} from 'react-i18next'

// ── Helpers ───────────────────────────────────────────────────────

/** Build CSS linear-gradient string from stops (always left-to-right for preview) */
const buildGradientCSS = (stops: GradientStop[]): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  const parts = sorted.map((s) => `${s.color} ${s.position * 100}%`)
  return `linear-gradient(90deg, ${parts.join(', ')})`
}

/** Interpolate color at position between sorted stops */
const interpolateColorAt = (stops: GradientStop[], pos: number): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  if (pos <= sorted[0].position) return sorted[0].color
  if (pos >= sorted[sorted.length - 1].position) return sorted[sorted.length - 1].color

  for (let i = 0; i < sorted.length - 1; i++) {
    const left = sorted[i]
    const right = sorted[i + 1]
    if (pos >= left.position && pos <= right.position) {
      const t = (pos - left.position) / (right.position - left.position)
      const lR = parseInt(left.color.slice(1, 3), 16)
      const lG = parseInt(left.color.slice(3, 5), 16)
      const lB = parseInt(left.color.slice(5, 7), 16)
      const rR = parseInt(right.color.slice(1, 3), 16)
      const rG = parseInt(right.color.slice(3, 5), 16)
      const rB = parseInt(right.color.slice(5, 7), 16)
      const r = Math.round(lR + (rR - lR) * t)
      const g = Math.round(lG + (rG - lG) * t)
      const b = Math.round(lB + (rB - lB) * t)
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    }
  }
  return sorted[0].color
}

// ── Constants ─────────────────────────────────────────────────────

const BAR_HEIGHT = 24
const MARKER_SIZE = 14

// ── GradientEditor ────────────────────────────────────────────────

const GradientEditor = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const stops = useAppSelector((s) => selectActiveScene(s).bgGradientStops)
  const [selectedId, setSelectedId] = useState<string | null>(stops[0]?.id ?? null)
  const barRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef<string | null>(null)

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const gradientCSS = useMemo(() => buildGradientCSS(stops), [stops])

  const selectedStop = stops.find((s) => s.id === selectedId) ?? null

  // ── Position from mouse event ───────────────────────────────────

  const getPositionFromEvent = useCallback((clientX: number): number => {
    if (!barRef.current) return 0
    const rect = barRef.current.getBoundingClientRect()
    const raw = (clientX - rect.left) / rect.width
    return Math.max(0, Math.min(1, raw))
  }, [])

  // ── Drag handlers ───────────────────────────────────────────────

  const handleMarkerPointerDown = useCallback(
    (stopId: string, e: ReactPointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedId(stopId)
      draggingRef.current = stopId

      const handlePointerMove = (ev: PointerEvent) => {
        if (!draggingRef.current) return
        const pos = getPositionFromEvent(ev.clientX)
        dispatch(updateGradientStop({id: draggingRef.current, changes: {position: Math.round(pos * 1000) / 1000}}))
      }

      const handlePointerUp = () => {
        draggingRef.current = null
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
        window.removeEventListener('pointercancel', handlePointerUp)
        dispatch(sealUndo())
      }

      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerUp)
    },
    [dispatch, getPositionFromEvent],
  )

  // ── Click on bar to add stop ────────────────────────────────────

  const handleBarClick = useCallback(
    (e: ReactMouseEvent) => {
      // Don't add stop if clicking on a marker
      if ((e.target as HTMLElement).dataset.marker) return
      const pos = getPositionFromEvent(e.clientX)
      const color = interpolateColorAt(stops, pos)
      const id = generateStopId()
      dispatch(addGradientStop({id, color, position: Math.round(pos * 1000) / 1000}))
      setSelectedId(id)
    },
    [dispatch, getPositionFromEvent, stops],
  )

  // ── Selected stop controls ──────────────────────────────────────

  const handleColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!selectedId) return
      dispatch(updateGradientStop({id: selectedId, changes: {color: e.target.value}}))
    },
    [dispatch, selectedId],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleDelete = useCallback(() => {
    if (!selectedId || stops.length <= 2) return
    dispatch(removeGradientStop(selectedId))
    setSelectedId(stops.find((s) => s.id !== selectedId)?.id ?? null)
  }, [dispatch, selectedId, stops])

  return (
    <Box sx={sx.root}>
      {/* Gradient bar with stop markers */}
      <Box ref={barRef} sx={{...sx.bar, background: gradientCSS}} onClick={handleBarClick}>
        {stops.map((stop) => {
          const isSelected = selectedId === stop.id
          return (
            <Box
              key={stop.id}
              data-marker="true"
              sx={{
                ...sx.marker,
                left: `${stop.position * 100}%`,
                borderColor: isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                borderWidth: isSelected ? 3 : 2,
                boxShadow: isSelected
                  ? '0 0 0 1.5px rgba(0,0,0,0.7), 0 0 6px rgba(255,255,255,0.4)'
                  : '0 0 0 1px rgba(0,0,0,0.3)',
                transform: isSelected ? 'translate(-50%, -50%) scale(1.25)' : 'translate(-50%, -50%)',
                zIndex: isSelected ? 2 : 1,
              }}
              style={{backgroundColor: stop.color, touchAction: 'none'}}
              onPointerDown={(e) => handleMarkerPointerDown(stop.id, e)}
            />
          )
        })}
      </Box>

      {/* Selected stop controls */}
      {selectedStop && (
        <Box sx={sx.controls}>
          <Box sx={sx.controlRow}>
            <Box sx={sx.colorControl}>
              <Typography sx={sx.label}>{t('scene.background.color')}</Typography>
              <input
                type="color"
                value={selectedStop.color}
                onChange={handleColorChange}
                onBlur={handleSealUndo}
                style={sx.colorInput}
              />
            </Box>
            {stops.length > 2 && (
              <IconButton size="small" onClick={handleDelete} sx={sx.deleteButton}>
                <DeleteIcon sx={{fontSize: 16}} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  bar: {
    position: 'relative' as const,
    height: BAR_HEIGHT,
    borderRadius: '6px',
    cursor: 'crosshair',
    border: `1px solid ${tokens.accordion.border}`,
    // Checkerboard behind gradient for opacity preview
    backgroundBlendMode: 'normal',
  },
  marker: {
    position: 'absolute' as const,
    top: '50%',
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    borderRadius: '2px',
    border: '2px solid',
    transform: 'translate(-50%, -50%)',
    cursor: 'grab',
    zIndex: 1,
    '&:active': {cursor: 'grabbing'},
  },
  controls: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorControl: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
    minWidth: 52,
    flexShrink: 0,
  },
  colorInput: {
    width: '100%',
    height: 28,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 6,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 2,
  } as CSSProperties,
  deleteButton: {
    color: tokens.text.muted,
    '&:hover': {color: '#ff6b6b'},
  },
})

export default GradientEditor
